import React, { useContext, createContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLtiProviders } from '@core/selectors/lti';
import { Assignment, User } from '@core/types/state';
import { Icons } from '@ublend-npm/aulaui-core';

import {
  HANDIN_PROVIDER,
  TURNITIN_PROVIDER,
  QUIZZES_PROVIDER,
  LTI1P3_PROVIDER,
  MANUAL,
  SSA_PROVIDER,
} from '@core/assignments/constants';
import launchTurnitinAssignment from '@app/actions/assignments/launchTurnitinAssignment.action';
import launchTurnitinSubmission from '@app/actions/assignments/launchTurnitinSubmission.action';
import { goToReports } from '@app/actions/navigation';
import showToast from '@core/toasts/operations/showToast.operation';
import { launchHandin } from '../utils/launchAssignment';
import { AssignmentProviderLogo } from '../AssignmentProviderLogo/AssignmentProviderLogo';
import { Lti1p3Launch } from '../../Assignments/Lti1p3Launch/Lti1p3Launch';

type AssignmentTypeProviderProps = {
  assignment?: Assignment;
  user?: User;
  spaceId?: string;
  children?: React.ReactNode;
};
type AssignmentLaunchOptions = {
  reportType?: 'feedback' | 'review';
  submissionExternalId?: string | null;
  submissionId?: string | null;
  studentId?: string | null;
  userId?: string | null;
};
type AssignmentLaunchFunction = (options?: AssignmentLaunchOptions) => void;

type ProviderContext = {
  type: string;
  title: string;
  renderImage: React.FunctionComponent;
  launchAssignment: AssignmentLaunchFunction;
  launchFeedback: AssignmentLaunchFunction;
  launchSubmission: AssignmentLaunchFunction;
};

type ProviderConfig = Record<string, ProviderContext>;

const emptyProviderContext: ProviderContext = {
  type: '',
  title: '',
  renderImage: () => null,
  launchAssignment: () => {},
  launchFeedback: () => {},
  launchSubmission: () => {},
};

const AssignmentTypeContext =
  createContext<ProviderContext>(emptyProviderContext);

const getQuizSpaceId = (
  assignment: Assignment | undefined,
  spaceId: string | undefined
) => (assignment?.classRoom ? assignment.classRoom.objectId : spaceId);

const AssignmentTypeProvider = ({
  assignment,
  spaceId,
  user,
  children,
}: AssignmentTypeProviderProps) => {
  const dispatch = useDispatch();
  const [launchLti1p3, setLaunchLti1p3] = useState(false);

  const launchTIISubmission = ({
    submissionExternalId,
  }: AssignmentLaunchOptions = {}) => {
    launchTurnitinSubmission({
      submissionId: submissionExternalId,
      spaceId,
    });
  };

  const launchQuizReport = ({
    submissionId,
    reportType,
  }: AssignmentLaunchOptions & { reportType: 'feedback' | 'review' }) => {
    goToReports({
      assignmentId: assignment?.id,
      spaceId: getQuizSpaceId(assignment, spaceId),
      submissionId,
      reportType,
    })();
  };

  const lti1p3Provider = useSelector(getLtiProviders).find(
    (provider) => provider.id === assignment?.providerId
  );

  const goToHandin = ({
    submissionId = null,
    studentId = null,
  }: AssignmentLaunchOptions = {}) =>
    launchHandin({
      assignmentId: assignment?.externalId,
      spaceId: assignment?.classRoom
        ? assignment?.classRoom?.objectId
        : spaceId,
      submissionId,
      studentId,
      isAnonymised: assignment?.isAnonymised,
    });

  const goToTurnitin = () =>
    launchTurnitinAssignment({
      user,
      assignment,
      spaceId,
    });

  const { ExtensionOutlined } = Icons;

  const providerConfig: ProviderConfig = {
    [HANDIN_PROVIDER]: {
      type: HANDIN_PROVIDER,
      title: 'Handin',
      renderImage: () => (
        <AssignmentProviderLogo providerType={HANDIN_PROVIDER} />
      ),
      launchAssignment: goToHandin,
      launchFeedback: goToHandin,
      launchSubmission: goToHandin,
    },
    [TURNITIN_PROVIDER]: {
      type: TURNITIN_PROVIDER,
      title: 'Turnitin',
      renderImage: () => (
        <AssignmentProviderLogo providerType={TURNITIN_PROVIDER} />
      ),
      launchAssignment: goToTurnitin,
      launchFeedback: goToTurnitin,
      launchSubmission: launchTIISubmission,
    },
    [QUIZZES_PROVIDER]: {
      type: QUIZZES_PROVIDER,
      title: 'quizzes',
      renderImage: () => (
        <AssignmentProviderLogo providerType={QUIZZES_PROVIDER} />
      ),
      launchAssignment: () => {},
      launchFeedback: ({ submissionId }: AssignmentLaunchOptions = {}) =>
        launchQuizReport({ submissionId, reportType: 'feedback' }),
      launchSubmission: ({ submissionId }: AssignmentLaunchOptions = {}) =>
        launchQuizReport({ submissionId, reportType: 'review' }),
    },
    [LTI1P3_PROVIDER]: {
      type: LTI1P3_PROVIDER,
      title: lti1p3Provider?.description || 'LTI tool',
      renderImage: () =>
        lti1p3Provider?.iconUrl ? (
          <AssignmentProviderLogo
            providerType={LTI1P3_PROVIDER}
            imageSrc={lti1p3Provider?.iconUrl}
          />
        ) : (
          <ExtensionOutlined />
        ),
      launchAssignment: () => setLaunchLti1p3(true),
      launchFeedback: () => {},
      launchSubmission: () => setLaunchLti1p3(true),
    },
    // For new responsive assignments, we will use the MANUAL provider type for assignments that are linked to SSA.
    // This will allow us to handle these assignments in a consistent way, and provide a default implementation for the launch functions.
    [MANUAL]: {
      type: MANUAL,
      title: SSA_PROVIDER,
      renderImage: () => <AssignmentProviderLogo providerType={SSA_PROVIDER} />,
      launchAssignment: () => {},
      launchFeedback: () => {},
      launchSubmission: () => {},
    },
  };

  const rawProvider = assignment?.provider
    ? String(assignment.provider)
    : undefined;
  const provider = rawProvider === 'handin' ? HANDIN_PROVIDER : rawProvider;
  const isConfiguredProvider = (
    value: string | undefined
  ): value is keyof ProviderConfig =>
    Boolean(
      value && Object.prototype.hasOwnProperty.call(providerConfig, value)
    );
  const contextValue: ProviderContext = isConfiguredProvider(provider)
    ? providerConfig[provider]
    : {
        ...emptyProviderContext,
        type: provider || '',
        title: provider || 'assignment',
        renderImage: () => <AssignmentProviderLogo providerType={''} />,
      };

  return (
    <AssignmentTypeContext.Provider value={contextValue}>
      {children}
      {launchLti1p3 && assignment?.providerId && assignment?.resourceLinkId && (
        <Lti1p3Launch
          assignmentId={assignment.id || assignment.objectId}
          onErrorLaunch={() => {
            setLaunchLti1p3(false);
            dispatch(
              showToast({
                message: 'There was an error when trying to open the LTI tool.',
              })
            );
          }}
          onSuccessLaunch={() => setLaunchLti1p3(false)}
          providerId={assignment.providerId}
          resourceLinkId={assignment.resourceLinkId}
          resourceLinkTitle={assignment.title}
        />
      )}
    </AssignmentTypeContext.Provider>
  );
};

export const useAssignmentTypeProvider = () => {
  return useContext(AssignmentTypeContext);
};

export default AssignmentTypeProvider;
